import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import { DocumentNode } from 'graphql';

import {
  SPAN_DESELECT_INSURANCE,
  SPAN_SELECT_INSURANCE,
} from '@constants/otel';
import { ProductType } from '@generated/gql/graphql';
import { useDebouncedIsLoading } from '@hooks/useDebouncedIsLoading';
import { useSnackbarError } from '@hooks/useSnackbarErrors';
import { useUpdatedTariffModalContext } from '@providers/basket/UpdatedTariffModalProvider/Context';
import { useTrace } from '@services/otel';
import { UpdatedTariffModalVariant } from '@src/types/bundle';

type UseInsuranceHandlerParams<T> = {
  quoteId: string;
  basketId: string;
  selectMutation: DocumentNode;
  deselectMutation: DocumentNode;
  onSelected: (data: T) => void;
  onSelectCompleted?: () => void;
  onDeselectCompleted?: () => void;
};

export const useInsuranceHandler = <T>({
  quoteId,
  basketId,
  selectMutation,
  deselectMutation,
  onSelected,
  onSelectCompleted,
  onDeselectCompleted,
}: UseInsuranceHandlerParams<T>) => {
  const { withTrace } = useTrace();
  const setSnackbarError = useSnackbarError();
  const { setModalState } = useUpdatedTariffModalContext();

  const [selectQuote, { loading: selectLoading }] = useMutation(selectMutation);
  const [deselectQuote, { loading: deselectLoading }] =
    useMutation(deselectMutation);

  const isLoading = useDebouncedIsLoading(
    [selectLoading, deselectLoading],
    false
  );

  const handleSelectQuote = async () => {
    try {
      const { data } = await selectQuote({
        variables: {
          input: {
            quoteId,
            basketId,
          },
        },
      });
      if (data) {
        onSelected(data as T);
      }

      setModalState(UpdatedTariffModalVariant.productAdded);
      onSelectCompleted?.();
    } catch (error) {
      setSnackbarError(error as ErrorLike);
    }
  };

  const handleDeselectQuote = async () => {
    try {
      await deselectQuote({
        variables: {
          input: {
            quoteId,
            basketId,
          },
        },
      });

      setModalState(UpdatedTariffModalVariant.productRemoved, [
        ProductType.Insurance,
      ]);
      onDeselectCompleted?.();
    } catch (error) {
      setSnackbarError(error as ErrorLike);
    }
  };

  const onSubmit = () =>
    withTrace(SPAN_SELECT_INSURANCE, handleSelectQuote, { quoteId });

  const onRemove = () =>
    withTrace(SPAN_DESELECT_INSURANCE, handleDeselectQuote, { quoteId });

  return {
    onSubmit,
    removeInsurance: onRemove,
    isLoading,
    isRemoving: deselectLoading,
  };
};
